import { useState, useEffect } from 'react';
import api from '../../../api/client';

export default function ForecastPage() {
  const [models,    setModels]    = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [form,      setForm]      = useState({ modelId: '', asOf: '', method: 'run_rate', lookbackMonths: 3 });
  const [loading,   setLoading]   = useState(false);
  const [running,   setRunning]   = useState(false);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    api.get('/planning/models').then(({ data }) => setModels(data.data?.items || [])).catch(() => {});
  }, []);

  async function loadForecasts(modelId) {
    if (!modelId) { setForecasts([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/planning/forecasts?model_id=${modelId}`);
      setForecasts(data.data?.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadForecasts(form.modelId); }, [form.modelId]);

  async function handleRun(e) {
    e.preventDefault();
    setRunning(true);
    setError(null);
    try {
      await api.post('/planning/forecasts', {
        modelId: form.modelId,
        asOf:    form.asOf,
        method:  form.method,
        lookbackMonths: Number(form.lookbackMonths),
      });
      await loadForecasts(form.modelId);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div data-testid="planning-forecast-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Forecast Runner</h2>

      <form onSubmit={handleRun} style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <select
            data-testid="planning-forecast-model-select"
            value={form.modelId}
            onChange={(e) => setForm((f) => ({ ...f, modelId: e.target.value }))}
            required
          >
            <option value="">— select model —</option>
            {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>

          <input
            data-testid="planning-forecast-asof-input"
            type="month"
            value={form.asOf}
            onChange={(e) => setForm((f) => ({ ...f, asOf: e.target.value }))}
            placeholder="As-of period (YYYY-MM)"
            required
          />

          <select
            data-testid="planning-forecast-method-select"
            value={form.method}
            onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}
          >
            <option value="run_rate">Run Rate</option>
            <option value="driver">Driver-Based</option>
            <option value="manual">Manual</option>
          </select>

          {form.method === 'run_rate' && (
            <input
              data-testid="planning-forecast-lookback-input"
              type="number"
              min={1}
              max={24}
              value={form.lookbackMonths}
              onChange={(e) => setForm((f) => ({ ...f, lookbackMonths: e.target.value }))}
              style={{ width: '80px' }}
              title="Lookback months"
            />
          )}

          <button
            type="submit"
            data-testid="planning-forecast-run"
            disabled={running || !form.modelId || !form.asOf}
          >
            {running ? 'Running…' : 'Run Forecast'}
          </button>
        </div>
        {error && <p style={{ color: 'red' }}>{error}</p>}
      </form>

      <h3>Past Forecasts</h3>
      {loading ? <p>Loading…</p> : (
        <table data-testid="planning-forecast-list" style={{ width: '100%' }}>
          <thead>
            <tr><th>As-Of</th><th>Method</th><th>Run at</th></tr>
          </thead>
          <tbody>
            {forecasts.map((f) => (
              <tr key={f.id} data-testid={`planning-forecast-row-${f.id}`}>
                <td>{f.as_of}</td>
                <td>{f.method}</td>
                <td>{f.created_at?.slice(0, 16)}</td>
              </tr>
            ))}
            {!forecasts.length && <tr><td colSpan={3}>No forecasts yet.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}
